'use client'

import { useStoreI18n } from '@/store'
import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices
} from '@/types'
import { Typo3EventDetailItem } from '@/types/typo3/sections/spotsanity-eventlist/detail'
import { Accordion, Button, Chip } from '@/ui/components'
import { SpotSanityDetailOpeningHours } from '@/ui/components/SpotSanity/SpotSanity'
import dynamic from 'next/dynamic'
import { Suspense, memo } from 'react'
import EventContact from './Info/EventContact'
import EventLinks from './Info/EventLinks'

const Map = dynamic(() => import('./Info/Map'), { ssr: false })

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
}

const getVariant = (data: Props['data']) =>
  data.type === 'spotsanity_eventlist' ? 'event' : 'default'

const hasBodyContent = (data: Props['data']) => {
  const item = data?.content?.item
  const variant = getVariant(data)
  const files = item?.files

  if (variant === 'event') {
    const eventItem = item as Typo3EventDetailItem | undefined
    const hasContact = !!(
      eventItem?.bookingContact ||
      eventItem?.eventContact ||
      eventItem?.infoContact ||
      eventItem?.organizerContact
    )
    const hasLinks = !!eventItem?.links?.some(
      (link) => link?.href && link?.title
    )
    if (hasContact || hasLinks) {
      return true
    }
  } else {
    const contact = item && 'contact' in item ? item?.contact : undefined
    if (contact?.street) {
      return true
    }
    if (contact?.phone || contact?.email || contact?.website) {
      return true
    }
  }

  if (files?.length) {
    return true
  }

  return false
}

const Body = ({ data }: Props) => {
  const item = data?.content?.item
  const contact =
    data.type === 'spotsanity_eventlist'
      ? item?.bookingContact
      : item && 'contact' in item && item?.contact
  const files = item?.files
  const D = useStoreI18n.use.dictionary()

  const variant = getVariant(data)

  return (
    <div className="gap-lg grid pb-6 max-sm:grid-cols-1 lg:grid-cols-2">
      {variant === 'event' && (
        <>
          <EventContact data={data.content.item as Typo3EventDetailItem} />
          <EventLinks data={data.content.item as Typo3EventDetailItem} />
        </>
      )}
      {variant !== 'event' && contact?.street && (
        <div className="gap-y-sm textstyle-md-regular flex flex-col items-start justify-start">
          <p className="textstyle-md-bold">{D['fe.plugin.hotel.address']}</p>
          <div className="flex flex-col items-start justify-start">
            {contact?.company && <p>{contact?.company}</p>}
            {contact?.street && <p>{contact?.street}</p>}
            {item?.locationText && <p>{item?.locationText}</p>}
            {contact?.zipcode && contact?.city && contact?.country && (
              <p>{`${contact?.zipcode} ${contact?.city} - ${contact?.country}`}</p>
            )}
          </div>
        </div>
      )}
      {(contact && contact?.phone) || contact?.email || contact?.website ? (
        <div className="gap-y-sm textstyle-md-regular flex flex-col items-start justify-start">
          <p className="textstyle-md-bold">{D['fe.plugin.hotel.contact']}</p>
          <div className="flex flex-col items-start justify-start">
            {contact?.phone && (
              <Button
                variant="text"
                nextLink={false}
                href={`tel:${contact?.phone}`}
              >
                {contact?.phone}
              </Button>
            )}
            {contact?.email && (
              <Button
                variant="text"
                nextLink={false}
                href={`mailto:${contact?.email}`}
              >
                {contact?.email}
              </Button>
            )}
            {contact?.website && (
              <Button
                variant="text"
                nextLink={false}
                target="_blank"
                href={contact?.website}
              >
                {contact?.website}
              </Button>
            )}
          </div>
        </div>
      ) : null}
      {!!files?.length && (
        <div className="gap-y-sm flex flex-col items-start justify-start">
          <p className="text-md-bold">{D['fe.events.download']}</p>
          <div className="flex flex-col items-start justify-start">
            {files.map((file, i) => (
              <Button
                key={i?.toString()}
                variant="text"
                target="_blank"
                nextLink={false}
                href={file.publicUrl}
                iconPosition="right"
                size="s"
              >
                {file?.properties?.title}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SpotSanityDetailInfo = ({ data, ...props }: Props) => {
  const item = data?.content?.item
  const contact =
    data.type === 'spotsanity_eventlist'
      ? item?.bookingContact || item?.organizerContact
      : item && 'contact' in item && item?.contact
  const facets = data?.content?.facets
  const D = useStoreI18n.use.dictionary()

  return (
    <div {...props}>
      {hasBodyContent(data) && (
        <Accordion.Container>
          <Accordion.Item
            value={'info'}
            head={<h3 className="h4">{D['fe.contact.info.title']}</h3>}
          >
            <Body data={data} />
          </Accordion.Item>
        </Accordion.Container>
      )}
      {item?.coordinates?.hasCoordinates && (
        <Suspense>
          <Map item={item} contact={contact} />
        </Suspense>
      )}
      {facets && (
        <Accordion.Container
          items={Object.keys(facets).map((key, i) => ({
            title: facets[key].name,
            isOpen: i === 0,
            value: 'facets-' + i?.toString()
          }))}
        >
          {Object.keys(facets).map((key, i) => {
            // Prüfen, ob einer der facetAttributes.title '0' ist
            const hasValidFacets = Object.values(
              facets[key].facetAttributes
            ).some((facet) => facet.title !== '0')

            // Accordion nur rendern, wenn hasValidFacets true ist
            if (!hasValidFacets) {
              return null
            }

            return (
              <Accordion.Item
                key={i?.toString()}
                value={'facets-' + i?.toString()}
                head={<h3 className="h4">{facets[key].name}</h3>}
              >
                <div className="gap-md gap-x-sm flex flex-wrap">
                  {Object.values(facets[key].facetAttributes).map(
                    (facet, i) => (
                      <Chip
                        key={i?.toString()}
                        variant="fill"
                        iconLeft="material-symbols:done"
                      >
                        {facet.title}
                      </Chip>
                    )
                  )}
                </div>
              </Accordion.Item>
            )
          })}
        </Accordion.Container>
      )}
      {item &&
        'listOfOpeningHours' in item &&
        item?.listOfOpeningHours?.hasOpeningsHours &&
        item?.listOfOpeningHours?.items?.length && (
          <Accordion.Container>
            <Accordion.Item
              value={'openingHours'}
              head={<h3 className="h4">{item?.listOfOpeningHours?.title}</h3>}
            >
              <SpotSanityDetailOpeningHours data={data} />
            </Accordion.Item>
          </Accordion.Container>
        )}
    </div>
  )
}

export default memo(SpotSanityDetailInfo)
